// src/pages/ClassListPage.jsx
import { useEffect, useMemo, useState, useCallback } from "react"
import { GraduationCap, Plus, Edit, Trash2, Users, Library, Search, LayoutGrid, Table as TableIcon, Loader, User } from "lucide-react"
import { motion } from "framer-motion"
import Drawer from "@/layout/Drawer"
import ConfirmModal from "@/widgets/ConfirmModal"
import ClassMembersModal from "@/widgets/ClassMembersModal"
import ClassCoursesModal from "@/widgets/ClassCoursesModal"
import { getClasses, saveClass, deleteClass } from "@/api/kuizy"
import { formatDateTime } from "@/settings/master"
import ViewSwitcher from "../../components/ui/ViewSwitcher"

const emptyClass = { id: "", code: "", name: "", description: "" }

export default function ClassListPage() {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [view, setView] = useState("table")
  const [searchIn, setSI] = useState("")
  const [searchTxt, setST] = useState("")
  const [drawer, setDrawer] = useState(false)
  const [draft, setDraft] = useState(emptyClass)
  const [saving, setSaving] = useState(false)
  const [delItem, setDelItem] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [memberModal, setMemberModal] = useState({ open: false, classId: null, className: "" })
  const [courseModal, setCourseModal] = useState({ open: false, classId: null, className: "" })
  const [alert, setAlert] = useState({ open: false, msg: "" })

  const load = useCallback(() => {
    setLoading(true)
    getClasses()
      .then(r => setRows(Array.isArray(r.data) ? r.data : []))
      .catch(() => setAlert({ open: true, msg: "โหลดข้อมูลไม่สำเร็จ" }))
      .finally(() => setLoading(false))
  }, [])
  useEffect(load, [load])

  const list = useMemo(() => rows.filter(c => !searchTxt || c.name.toLowerCase().includes(searchTxt.toLowerCase()) || c.code.toLowerCase().includes(searchTxt.toLowerCase())), [rows, searchTxt])

  const save = e => {
    e.preventDefault()
    if (saving) return
    setSaving(true)
    saveClass(draft)
      .then(() => {
        setDrawer(false)
        setDraft(emptyClass)
        load()
      })
      .catch(() => setAlert({ open: true, msg: "บันทึกไม่สำเร็จ" }))
      .finally(() => setSaving(false))
  }

  const confirmDelete = () => {
    if (deleting) return
    setDeleting(true)
    deleteClass(delItem.id)
      .then(load)
      .catch(() => setAlert({ open: true, msg: "ไม่สามารถลบได้" }))
      .finally(() => {
        setDeleting(false)
        setDelItem(null)
      })
  }

  return (
    <motion.section initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} className="flex flex-col gap-6 p-4 sm:p-6">
      <motion.header initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.05 }} className="flex flex-wrap items-end gap-3 justify-between">
        <h1 onClick={load} className="flex items-center gap-2 text-lg font-semibold cursor-pointer">
          <GraduationCap size={20} /> จัดการห้องเรียน ({rows.length})
        </h1>
        <div className="flex flex-wrap gap-2">
          <ViewSwitcher view={view} setView={setView} />
          <button disabled={saving} onClick={() => { setDraft(emptyClass); setDrawer(true) }} className="inline-flex items-center gap-1 bg-primary hover:bg-secondary text-white rounded-lg px-3 py-2 text-sm disabled:opacity-50">
            {saving ? <Loader size={16} className="animate-spin" /> : <Plus size={16} />}
            เพิ่มห้องเรียน
          </button>
        </div>
      </motion.header>

      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.05 }} className="flex flex-wrap items-end gap-4 bg-white rounded-xl shadow-md p-4">
        <div className="flex flex-col gap-1 flex-1 min-w-[220px]">
          <label className="flex items-center gap-1 text-sm">
            <Search size={16} /> ค้นหา
          </label>
          <div className="relative">
            <input value={searchIn} onChange={e => setSI(e.target.value)} onKeyDown={e => e.key === "Enter" && setST(searchIn)} className="w-full border border-gray-300 rounded-lg px-10 py-2 text-sm focus:ring-2 focus:ring-primary" placeholder="ค้นหาชื่อห้องเรียน / ชื่อย่อห้องเรียน" />
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          </div>
        </div>
        <button onClick={() => setST(searchIn)} className="inline-flex items-center gap-1 bg-secondary hover:bg-primary text-white rounded-lg px-3 py-2 text-sm">
          <Search size={16} /> ค้นหา
        </button>
      </motion.div>

      {view === "card" && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.35, delay: 0.1 }} className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {loading ? (
            <div className="col-span-full flex justify-center py-12">
              <Loader className="animate-spin" />
            </div>
          ) : list.length === 0 ? (
            <div className="col-span-full text-center text-gray-500 py-12">– ไม่มีข้อมูล –</div>
          ) : (
            list.map(c => (
              <motion.div key={c.id} whileHover={{ y: -4 }} className="bg-white rounded-2xl shadow-md p-6 flex flex-col gap-6">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold">{c.name}</p>
                    <p className="text-sm text-gray-500">{c.code}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => { setDraft(c); setDrawer(true) }} className="text-primary hover:text-primary/80">
                      <Edit size={18} />
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <button onClick={() => setMemberModal({ open: true, classId: c.id, className: c.name })} className="bg-primary/10 rounded-lg p-3 flex flex-col items-center hover:bg-neutral-200">
                    <User size={18} className="text-secondary" />
                    <span className="text-[10px] text-gray-600 mt-1">ผู้เรียน</span>
                    <strong className="text-lg">{c.member_count || 0}</strong>
                  </button>
                  <button onClick={() => setCourseModal({ open: true, classId: c.id, className: c.name })} className="bg-primary/10 rounded-lg p-3 flex flex-col items-center hover:bg-neutral-200">
                    <Library size={18} className="text-secondary" />
                    <span className="text-[10px] text-gray-600 mt-1">หลักสูตร</span>
                    <strong className="text-lg">{c.course_count || 0}</strong>
                  </button>
                </div>
                <div className="flex items-end justify-between">
                  <p className="text-xs text-gray-500">
                    <span>สร้างเมื่อ {formatDateTime(c.created_at)}</span>
                    <br />
                    <span>แก้ไขล่าสุด {formatDateTime(c.updated_at || c.created_at)}</span>
                  </p>
                  <button onClick={() => setDelItem(c)} className="text-red-600 hover:text-red-500">
                    <Trash2 size={18} />
                  </button>
                </div>
              </motion.div>
            ))
          )}
        </motion.div>
      )}

      {view === "table" && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.35, delay: 0.1 }} className="bg-white rounded-xl shadow-md overflow-x-auto -mx-4 sm:mx-0">
          <table className="min-w-[820px] w-full text-sm">
            <thead className="bg-gray-100 text-gray-600 whitespace-nowrap">
              <tr>
                <th className="py-2 px-3 w-12 text-left">#</th>
                <th className="py-2 px-3 text-left">ชื่อย่อ</th>
                <th className="py-2 px-3 text-left">ชื่อห้องเรียน</th>
                <th className="py-2 px-3 text-center">ผู้เรียน</th>
                <th className="py-2 px-3 text-center">หลักสูตร</th>
                <th className="py-2 px-3 text-right hidden md:table-cell">สร้างเมื่อ</th>
                <th className="py-2 px-3 text-right hidden md:table-cell">แก้ไขล่าสุด</th>
                <th className="py-2 px-3 w-40 text-right">จัดการ</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="py-6 text-center">
                    <Loader className="animate-spin" />
                  </td>
                </tr>
              ) : list.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-6 text-center text-gray-500">
                    – ไม่มีข้อมูล –
                  </td>
                </tr>
              ) : (
                list.map((c, i) => (
                  <tr key={c.id} className="odd:bg-white even:bg-gray-50 whitespace-nowrap">
                    <td className="py-2 px-3">{i + 1}</td>
                    <td className="py-2 px-3">{c.code}</td>
                    <td className="py-2 px-3">
                      <button onClick={() => setMemberModal({ open: true, classId: c.id, className: c.name })} className="text-primary hover:underline">
                        {c.name}
                      </button>
                    </td>
                    <td className="py-2 px-3 text-center">
                      <button onClick={() => setMemberModal({ open: true, classId: c.id, className: c.name })} className="text-primary font-bold hover:underline">
                        {c.member_count || 0}
                      </button>
                    </td>
                    <td className="py-2 px-3 text-center">
                      <button onClick={() => setCourseModal({ open: true, classId: c.id, className: c.name })} className="text-primary font-bold hover:underline">
                        {c.course_count || 0}
                      </button>
                    </td>
                    <td className="py-2 px-3 text-right hidden md:table-cell">{formatDateTime(c.created_at)}</td>
                    <td className="py-2 px-3 text-right hidden md:table-cell">{formatDateTime(c.updated_at)}</td>
                    <td className="py-2 px-3 text-right space-x-1">
                      <button title="ผู้เรียน" onClick={() => setMemberModal({ open: true, classId: c.id, className: c.name })} className="text-green-600 hover:text-green-500">
                        <User size={16} />
                      </button>
                      <button title="หลักสูตร" onClick={() => setCourseModal({ open: true, classId: c.id, className: c.name })} className="text-indigo-600 hover:text-indigo-500">
                        <Library size={16} />
                      </button>
                      <button title="แก้ไข" onClick={() => { setDraft(c); setDrawer(true) }} className="text-primary hover:text-primary/80">
                        <Edit size={16} />
                      </button>
                      <button title="ลบ" onClick={() => setDelItem(c)} className="text-red-600 hover:text-red-500">
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </motion.div>
      )}

      <Drawer open={drawer} title={draft.id ? "แก้ไขห้องเรียน" : "เพิ่มห้องเรียน"} onClose={() => setDrawer(false)}>
        <form onSubmit={save} className="flex flex-col gap-4">
          <label className="flex items-center gap-1 text-sm">
            <GraduationCap size={14} /> ชื่อห้องเรียน <span className="text-red-500 font-bold">*</span>
          </label>
          <input value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary" />
          <label className="flex items-center gap-1 text-sm">
            <GraduationCap size={14} /> ชื่อย่อห้องเรียน <span className="text-red-500 font-bold">*</span>
          </label>
          <input value={draft.code} onChange={e => setDraft({ ...draft, code: e.target.value })} className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary" />
          {/* <label className="flex items-center gap-1 text-sm">
            รายละเอียด
          </label>
          <textarea value={draft.description} onChange={e => setDraft({ ...draft, description: e.target.value })} className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary h-24" />
          */}
          <button disabled={saving || !draft.code.trim() || !draft.name.trim()} className="bg-primary hover:bg-secondary text-white rounded-lg px-4 py-2 disabled:opacity-50">{saving ? <Loader size={16} className="animate-spin" /> : "บันทึก"}</button>
        </form>
      </Drawer>

      {memberModal.open && <ClassMembersModal {...memberModal} onClose={() => { setMemberModal({ open: false, classId: null, className: "" }); load(); }} />}
      {courseModal.open && <ClassCoursesModal {...courseModal} onClose={() => { setCourseModal({ open: false, classId: null, className: "" }); load(); }} />}
      {delItem && <ConfirmModal type="confirm" title="ยืนยันการลบ" message={`ลบห้องเรียน "${delItem.name}" ?`} onConfirm={confirmDelete} onCancel={() => setDelItem(null)} loading={deleting} />}
      {alert.open && <ConfirmModal type="alert" title="แจ้งเตือน" message={alert.msg} onClose={() => setAlert({ open: false, msg: "" })} />}
    </motion.section>
  )
}
